import { Box, Container, Grid, Typography } from '@mui/material';
import {
  FormationsPrimaryButton,
  FormationsSecondaryButton,
} from 'components/common/Buttons';
import { Loading } from 'components/common/Loading';
import { Routes } from 'fnRoutes';
import { useNavigate } from 'hooks/api';
import {
  useAcceptInvitation,
  useDeclineInvitation,
  useInvitations,
} from 'hooks/api/useInvitations';
import {
  InvitationType,
  InvitationWithDestinationDetails,
} from 'models/invitations';
import { useCallback, useEffect } from 'react';
import { queryClient } from 'states/reactQueryClient';

const getInvitationText = (invitation: InvitationWithDestinationDetails) => {
  switch (invitation.type) {
    case InvitationType.Household:
      return `${invitation.fromUserName} invites you to join their household, with ${invitation.access} access`;
    case InvitationType.Business:
      return `${invitation.fromUserName} invites you to join their business ${invitation.destinationName}, with ${invitation.access} access`;
    default:
      return '';
  }
};

export const Invitations = () => {
  const navigate = useNavigate();
  const { invitations, isLoading } = useInvitations();
  const { acceptInvitationAsync } = useAcceptInvitation();
  const { declineInvitationAsync } = useDeclineInvitation();
  const redirectHandlerToHome = useCallback(() => {
    navigate(Routes.HOME());
  }, [navigate]);

  const handleAcceptInvitation = async (id: string) => {
    await acceptInvitationAsync(id);
    await queryClient.invalidateQueries({
      queryKey: ['currentCompanyList'],
    });
    redirectHandlerToHome();
  };

  const handleDeclineInvitation = async (id: string) => {
    await declineInvitationAsync(id);
    redirectHandlerToHome();
  };

  useEffect(() => {
    if (!isLoading && invitations && invitations.length === 0) {
      redirectHandlerToHome();
    }
  }, [invitations, isLoading, redirectHandlerToHome]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container>
      <Grid container justifyContent="center">
        <Grid sx={{ maxWidth: 800 }}>
          <Box mt={8} sx={{ textAlign: 'center' }}>
            {invitations && invitations.length > 0 ? (
              <>
                {invitations.map((invitation) => (
                  <Box key={invitation.id}>
                    <Typography>{getInvitationText(invitation)}</Typography>

                    <FormationsSecondaryButton
                      onClick={() => handleDeclineInvitation(invitation.id)}
                      data-testid="btn-homepage"
                    >
                      Decline
                    </FormationsSecondaryButton>

                    <FormationsPrimaryButton
                      onClick={() => handleAcceptInvitation(invitation.id)}
                      data-testid="btn-homepage"
                    >
                      Accept
                    </FormationsPrimaryButton>
                  </Box>
                ))}
              </>
            ) : (
              <Box mt={2}>
                <FormationsPrimaryButton
                  onClick={redirectHandlerToHome}
                  data-testid="btn-homepage"
                >
                  Redirect to Home
                </FormationsPrimaryButton>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Invitations;
